import { Command } from '@nest/internal-frontend';
import { Skeleton } from '@nest/uikit-react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTerminalClient } from '../client-rpc-router';
import { TerminalScreen } from '../components/TerminalScreen';
import { useNumberParam } from '../helpers';

export default function ConsignmentNotesCompletedListPage() {
	const client = useTerminalClient();
	const navigate = useNavigate();
	const carrierId = useNumberParam('carrierId');

	const carrierQuery = useQuery({
		queryKey: ['carrier', carrierId],
		queryFn: () => client.carriers.getById.execute({ id: carrierId! }),
		enabled: !!carrierId
	});

	const listQuery = useInfiniteQuery({
		queryKey: ['consignment-notes-completed-list'],
		queryFn: ({ pageParam }) => client.consignmentNotes.getCompletedConsignmentNotesQuery.execute({ offset: pageParam, limit: 10, carrierId: carrierId! }),
		getNextPageParam: (lastPage, allPages) => (lastPage.length === 10 ? allPages.length * 10 : undefined),
		initialPageParam: 0,
		enabled: !!carrierQuery.data
	});

	const commands: Command[] = [];

	const list = useMemo(() => listQuery.data?.pages.flatMap(p => p) ?? [], [listQuery.data]);

	return (
		<TerminalScreen showBackButton topBar={<div>{carrierQuery.data?.name} – hotové</div>} commands={commands}>
			<div className="p-2 flex flex-col gap-2">
				{carrierQuery.isLoading || listQuery.isLoading ? (
					<div className="space-y-2">
						<Skeleton className="h-10 w-full" />
						<Skeleton className="h-10 w-full" />
					</div>
				) : (
					<>
						{list.map(item => {
							const packagesCount = item.lines?.length ?? 0;
							const totalWeight = (item.lines ?? []).reduce((sum, line) => {
								const w = line.packageUnit?.weightKg ?? 0;
								return sum + Number(w ?? 0);
							}, 0);
							const weightText = packagesCount ? ` • Váha: ${totalWeight.toFixed(2)} kg` : '';
							const completedAt = item.completedAt ? new Date(item.completedAt) : null;
							const completedAtText = completedAt ? completedAt.toLocaleString() : '';

							return (
								<button
									key={item.id}
									className="w-full text-left border rounded p-2 hover:bg-muted/50"
									onClick={() => navigate(`/consignment-notes/carriers/${carrierId}/list/${item.id}`)}
								>
									<div className="font-medium">{item.containerNumber}</div>
									<div className="text-xs text-muted-foreground">
										{item.carrier?.name ?? ''}
										{packagesCount ? ` • Balíků: ${packagesCount}` : ''}
										{weightText}
										{completedAtText ? ` • Ukončeno: ${completedAtText}` : ''}
									</div>
								</button>
							);
						})}
						{list.length === 0 && <div className="p-6 text-center text-muted-foreground">Žádné záznamy</div>}
					</>
				)}
			</div>
		</TerminalScreen>
	);
}
